@let isPreview = isPreview$ | async;
@let isEcommerceMode = isEcommerceMode$ | async;
@let currentPage = currentPage$ | async;
@let isAdmin = isAdmin$ | async;
@let site = site$ | async;
@let siteContent = siteContent$ | async;
@let pageContent = pageContent$ | async;
@let isSaving = isSaving$ | async;
@let showOnboardingSteps = showOnboardingSteps$ | async;
@let isAutoSavePaused = isAutoSavePaused$ | async;
@let isCurrentPageDeleted = isCurrentPageDeleted$ | async;

<sp-base-header>
    @if (showOnboardingSteps) {
        <div onboarding>
            <div class="create-site-onboarding-wrapper">
                <div class="onboarding-step confirmed">
                    <span class="sp-icon icon-check-circle"> </span>
                    <span>{{ "create_site_onboarding_step_1_title" | translate }}</span>
                </div>
                <div class="onboarding-step confirmed">
                    <span class="sp-icon icon-check-circle"> </span>
                    <span>{{ "create_site_onboarding_step_2_title" | translate }}</span>
                </div>
                <div class="onboarding-step current">
                    <div class="step-number">3</div>
                    <span>{{ "create_site_onboarding_step_3_title" | translate }}</span>
                </div>
            </div>
        </div>
    }
    <div class="start-section" start>
        @if (currentPage) {
            <div class="site-part">
                <div class="section-left">
                    <sp-base-logo [logoLinkUrl]="siteServiceLink" [targetBlank]="true"> </sp-base-logo>
                </div>
            </div>
        }
        @if (!isEcommerceMode) {
            <div
                class="page-part"
                [title]="'pages_title' | translate"
                [class.active]="isShowPageDrawer$ | async"
                [class.previewMode]="isPreview"
                (click)="showPageDrawer(isPreview)"
            >
                <div class="navbar-divider-arrow"></div>
                <img class="part-icon" src="./assets/img/websites/header-bar/lpc-list-icon.svg" />
                <div class="text-wrapper">
                    <div class="page-name text-nowrap text-dotted-crop" [title]="currentPage?.name">
                        {{ currentPage?.name }}
                    </div>
                </div>
                <img class="part-caret" src="./assets/img/websites/header-bar/lpc-triangle-icon.svg" />
            </div>
        }

        @if (!isPreview) {
            <div
                class="navigation-part"
                [title]="'navigation_title' | translate"
                [class.active]="isShowNavigationDrawer$ | async"
                (click)="showNavigationDrawer()"
            >
                <div class="navbar-divider-arrow"></div>
                <img class="part-icon" src="./assets/img/websites/header-bar/lpc-layer-icon.svg" />
                <div class="text-wrapper">
                    {{ "navigation_title" | translate }}
                </div>
                <img class="part-caret" src="./assets/img/websites/header-bar/lpc-triangle-icon.svg" />
            </div>
        }
        @if ((isUserTariffFree$ | async) && (isPersonalSite$ | async)) {
            <web-upgrade-plan-popover></web-upgrade-plan-popover>
        }
    </div>
    <div class="nav navbar-nav navbar-right navbar-nav-constructor" end>
        @if (!isPreview && site && currentPage) {
            <div class="saving-status-container">
                @if (isHavingAnyUnpublishedChanges(site, currentPage, siteContent, pageContent) && !showLabel) {
                    <span class="saving-status">
                        {{ "have_unpublished_changes" | translate }}
                    </span>
                }
                @if (
                    (!isHavingAnyUnpublishedChanges(site, currentPage, siteContent, pageContent) &&
                        !showLabel &&
                        !isPageNew(currentPage) &&
                        isPageActive(currentPage)) ||
                    showLabel === "published"
                ) {
                    <span class="saving-status">
                        {{ "all_changes_published" | translate }}
                    </span>
                }
                @if (
                    !isHavingAnyUnpublishedChanges(site, currentPage, siteContent, pageContent) &&
                    !showLabel &&
                    (isPageNew(currentPage) || isPageNotActive(currentPage))
                ) {
                    <span class="saving-status">
                        {{ "all_changes_saved" | translate }}
                    </span>
                }
                @if (showLabel === "not_saved") {
                    <span class="saving-status">
                        {{ "not_saved" | translate }}
                    </span>
                }
                @if (showLabel === "saved") {
                    <span class="saving-status">
                        <span class="sp-icon icon-check color-success"></span>
                        {{ "saved" | translate }}
                    </span>
                }
                @if (!isEcommerceMode) {
                    <div class="navbar-site-url">
                        <span
                            class="badge badge-status"
                            [class.status-green]="isPageActive(currentPage)"
                            [class.status-default]="isPageNew(currentPage)"
                        ></span>
                        <div class="url-container text-nowrap text-dotted-crop">
                            @if (isPageActive(currentPage)) {
                                <a [href]="currentPage?.url" target="_blank" [title]="currentPage?.url">
                                    {{ currentPage?.url }}
                                    <small class="sp-icon icon-outgoing new-window-icon"> </small>
                                </a>
                            }
                            @if (isPageNew(currentPage) || isPageNotActive(currentPage)) {
                                <span class="unpublic-text small">
                                    {{ "page_unpublic" | translate }}
                                </span>
                            }
                        </div>
                    </div>
                }
            </div>
        } @else if (!isPreview && site && isCurrentPageDeleted) {
            <div class="saving-status-container">
                <span class="saving-status">
                    {{ "not_saved" | translate }}
                </span>
            </div>
        }

        @if (!isPreview && !isAdmin) {
            <div>
                <sp-partners-catalog [contentSource]="contentSource" [isReadyToShow$]="introFinished$" />
            </div>
        }

        @if (!isPreview && site && currentPage) {
            <div dropdown class="site-version-block" id="siteVersionDropdown">
                <button type="button" class="btn dropdown-toggle sp-navbar-btn btn-sm-mobile mr-5" dropdownToggle>
                    <span class="sp-icon icon-revert"> </span>
                    <span class="caret ml-5"></span>
                </button>
                <ul class="dropdown-menu dropdown-menu-left" *dropdownMenu>
                    <li class="dropdown-title small text-muted">
                        {{ "page_version" | translate }}
                    </li>
                    @if (isDraft(site, siteContent, pageContent)) {
                        <li>
                            <a>
                                <span class="sp-icon icon-ap-time"> </span> {{ "draft" | translate }}
                                <div class="small text-muted">{{ "auto_saved" | translate }}</div>
                            </a>
                        </li>
                    }
                    @if (isHavingAnyUnpublishedChanges(site, currentPage, siteContent, pageContent)) {
                        <li (click)="showSavedVersion()">
                            <a>
                                <span class="sp-icon icon-earth"> </span> {{ "revert_to_published" | translate }}
                                <div class="small text-muted">
                                    {{ lastPublishedDate | spTimeFormat: (locale$ | async) }}
                                </div>
                            </a>
                        </li>
                    }
                    @if (isLatestVersion(site, siteContent, pageContent)) {
                        <li>
                            <a>
                                <span class="sp-icon icon-earth"> </span> {{ "published" | translate }}
                                <div class="small text-muted">
                                    {{ lastPublishedDate | spTimeFormat: (locale$ | async) }}
                                </div>
                            </a>
                        </li>
                    }
                </ul>
            </div>
        }

        @if (!isPreview) {
            <div class="btn-group animate mr-5" role="group" aria-label="...">
                <button
                    type="button"
                    class="btn btn-default btn-sm-mobile sp-navbar-btn"
                    [class.disabled]="isPrevEmpty$ | async"
                    [tooltip]="undoTitle"
                    [delay]="1000"
                    (click)="undo()"
                >
                    <i class="sp-icon icon-undo"></i>
                </button>
                <button
                    type="button"
                    class="btn btn-default btn-sm-mobile sp-navbar-btn mr-0"
                    [class.disabled]="isNextEmpty$ | async"
                    [tooltip]="redoTitle"
                    [delay]="1000"
                    (click)="redo()"
                >
                    <i class="sp-icon icon-redo"></i>
                </button>
            </div>
        }

        @if (isPreview) {
            <div class="btn-group mr-5">
                <button
                    type="button"
                    class="btn preview-mode-btn sp-navbar-btn"
                    [class.active]="isPreviewDesktop$ | async"
                    (click)="changePreviewMode(previewMode.desktop)"
                >
                    <i class="sp-icon icon-desktop"></i>
                </button>
                <button
                    type="button"
                    class="btn preview-mode-btn sp-navbar-btn"
                    [class.active]="isPreviewTablet$ | async"
                    (click)="changePreviewMode(previewMode.tablet)"
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect
                            x="2"
                            y="1"
                            width="12"
                            height="14"
                            rx="3"
                            stroke="#fff"
                            stroke-width="2"
                            stroke-miterlimit="10"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M10.5 12C10.5 12.5523 10.0523 13 9.5 13L6.5 13C5.94772 13 5.5 12.5523 5.5 12C5.5 11.4477 5.94772 11 6.5 11L9.5 11C10.0523 11 10.5 11.4477 10.5 12Z"
                            fill="#fff"
                        />
                    </svg>
                </button>
                <button
                    type="button"
                    class="btn preview-mode-btn sp-navbar-btn mr-0"
                    [class.active]="isPreviewMobile$ | async"
                    (click)="changePreviewMode(previewMode.mobile)"
                >
                    <i class="sp-icon icon-mobile"></i>
                </button>
            </div>
        }

        @if (isPreview) {
            <button
                type="button"
                class="btn btn-action btn-default btn-preview-active navbar-btn sp-navbar-btn mr-5"
                (click)="hidePreview()"
            >
                <span class="sp-icon icon-arrow-left1"></span>
                <span class="btn-text">{{ "to_the_editor" | translate }}</span>
            </button>
        }

        @if (!isPreview) {
            <button
                type="button"
                class="btn btn-default btn-preview navbar-btn sp-navbar-btn mr-5"
                (click)="showPreview()"
            >
                <span class="sp-icon icon-watch"> </span>
                <span class="btn-text">{{ "preview" | translate }}</span>
            </button>
        }

        @if (!isPreview && !isEcommerceMode) {
            <button
                id="introThemeSettings"
                [class.disabled]=""
                class="btn btn-default sp-navbar-btn click-outside-close-prevent hidden-xs hidden-sm mr-5"
                type="button"
                (click)="showThemeSettings()"
            >
                <img src="./assets/img/websites/lpc-color-set.svg" alt="color-set" />
            </button>
        }

        <div id="publishButton">
            @if (isPublishButtonAvailable$ | async) {
                <button
                    type="button"
                    class="btn btn-success btn-sm-mobile mr-5"
                    (click)="publish()"
                    [class.disabled]="isAutoSavePaused"
                    [class.btn-loading]="isSaving || (isPublishing$ | async)"
                >
                    @if (!isEcommerceMode) {
                        {{ "publish" | translate }}
                    }
                    @if (isEcommerceMode) {
                        {{ "save" | translate }}
                    }
                </button>
            } @else {
                @if (isPublicationLimitReached$ | async) {
                    <div
                        class="blocked-publish-button"
                        [tooltip]="tooltip"
                        placement="bottom"
                        triggers="mouseover:mouseleave"
                    >
                        <span class="badge badge-paid">&ngsp;</span>
                        <button type="button" class="btn btn-success mr-5 btn-sm-mobile disabled">
                            &ngsp; &ngsp;{{ "publish" | translate }}
                        </button>
                    </div>
                    <ng-template #tooltip>
                        @if (isNewSite$ | async) {
                            <span class="system-variable-label">
                                {{ "reached_limit_of_publications" | translate }}
                            </span>
                        } @else {
                            @if ((availablePageCount$ | async) > 0) {
                                <span>
                                    {{ "reached_limit_of_publications" | translate }}
                                </span>
                            } @else {
                                <span>
                                    {{ "reached_limit_of_pages_publications" | translate }}
                                </span>
                            }
                        }
                    </ng-template>
                } @else {
                    <button
                        type="button"
                        class="btn btn-success mr-5"
                        [class.disabled]="!isSaving"
                        [class.btn-loading]="isSaving"
                    >
                        @if (!isEcommerceMode) {
                            {{ "publish" | translate }}
                        }
                        @if (isEcommerceMode) {
                            {{ "save" | translate }}
                        }
                    </button>
                }
            }
        </div>

        <button type="button" class="btn btn-close-constructor" (click)="close()">
            <i class="sp-icon icon-closed"></i>
        </button>
    </div>
</sp-base-header>

@if (!isAdmin) {
    <sp-nps-loader
        [ready]="isReady$ | async"
        [isDirty]="isDirty$ | async"
        [pageId]="currentPage?.id"
        (init)="initNps($event)"
    >
    </sp-nps-loader>
}
